<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Create New Project</h4>
            <form>
              <div class="form-group row mb-4">
                <label for="jobNum" class="col-form-label col-lg-2"
                  >Job Number</label
                >
                <div class="col-lg-10">
                  <input
                    type="text"
                    maxlength="10"
                    id="jobNum"
                    class="form-control"
                    rows="1"
                    placeholder="Enter Job Number..."
                    v-model="jobNumber"
                    :class="{ 'is-invalid': addJob_errors.jobNumError }"
                  />
                  <div
                    v-if="addJob_errors.jobNumError"
                    class="invalid-feedback"
                  >
                    <span>{{ addJob_errors.jobNumMessage }}</span>
                  </div>
                </div>
              </div>

              <div class="form-group row mb-4">
                <label for="projectdesc" class="col-form-label col-lg-2"
                  >Project Description</label
                >
                <div class="col-lg-10">
                  <textarea
                    id="projectdesc"
                    class="form-control"
                    rows="3"
                    placeholder="Enter Project Description..."
                    v-model="jobDescription"
                    :class="{ 'is-invalid': addJob_errors.descError }"
                  ></textarea>
                  <div v-if="addJob_errors.descError" class="invalid-feedback">
                    <span>{{ addJob_errors.descMessage }}</span>
                  </div>
                </div>
              </div>

              <div class="form-group row mb-4">
                <label for="projectcust" class="col-form-label col-lg-2"
                  >Customer</label
                >
                <div class="col-lg-8 col-sm-8">
                  <v-select
                    :options="customers"
                    label="customer"
                    v-model="projectCustomer"
                    :class="{
                      'is-invalid ddl-is-invalid': addJob_errors.customerError,
                    }"
                  ></v-select>
                  <div
                    v-if="addJob_errors.customerError"
                    class="invalid-feedback"
                  >
                    <span>{{ addJob_errors.customerMessage }}</span>
                  </div>
                </div>
                <div class="col-lg-2 mt-4 mt-lg-0">
                  <b-button
                    variant="primary"
                    class="btn btn-label btn-primary float-right"
                    v-b-modal.modal_addCustomer
                    ><i class="bx bx-plus label-icon"></i>Add Customer</b-button
                  >
                </div>
              </div>

              <div class="form-group row mb-4">
                <label class="col-form-label col-lg-2">Project Date</label>
                <div class="col-lg-10">
                  <date-picker
                    v-model="date"
                    append-to-body
                    lang="en"
                    :class="{ 'is-invalid': addJob_errors.dateError }"
                    :input-class="{
                      'form-control mx-input date-is-invalid':
                        addJob_errors.dateError,
                      'form-control mx-input': !addJob_errors.dateError,
                    }"
                  ></date-picker>
                  <div v-if="addJob_errors.dateError" class="invalid-feedback">
                    <span>{{ addJob_errors.dateMessage }}</span>
                  </div>
                </div>
              </div>

              <div class="form-group row mb-4">
                <label class="col-form-label col-lg-2"
                  >Supervisor (Optional)</label
                >
                <div class="col-lg-10">
                  <v-select
                    :options="employees"
                    label="name"
                    v-model="supervisor"
                  ></v-select>
                </div>
              </div>

              <div class="form-group row mb-4">
                <label class="col-form-label col-lg-2"
                  >Job Type (Optional)</label
                >
                <div class="col-lg-10">
                  <v-select
                    :options="jobTypeOptions"
                    v-model="jobType"
                    transition=""
                  ></v-select>
                </div>
              </div>
              <div class="form-group row mb-4">
                <label for="department" class="col-form-label col-lg-2"
                  >Department (Optional)</label
                >
                <div class="col-lg-10">
                  <input
                    type="text"
                    id="department"
                    class="form-control"
                    rows="1"
                    v-model.trim="jobDepartment"
                  />
                </div>
              </div>

              <div
                v-if="jobType === 'Quoted (Fixed Rate)'"
                class="form-group row mb-4"
              >
                <label class="col-form-label col-lg-2">Quoted Price</label>
                <div class="col-lg-10">
                  <b-form-input
                    v-model="jobQuote"
                    @blur="
                      () => {
                        if (Number(jobQuote) > max) {
                          jobQuote = max;
                        }
                      }
                    "
                    type="number"
                    min="0"
                    max="100000000"
                  ></b-form-input>
                </div>
              </div>

              <div class="form-group row mb-4">
                <label
                  class="col-form-label col-lg-2"
                  id="lbl_addJob_revenueCalculations"
                  >Revenue Calculations
                  <i class="bx bxs-help-circle"></i>
                  <b-tooltip target="lbl_addJob_revenueCalculations"
                    >Default revenue calculations will base the job's revenue
                    off of the job type. Time x Material jobs will calculate
                    revenue based off of the time, products, and procurement
                    revenue. Quoted (Fixed Rate) jobs will calculate revenue
                    based off of the specified Quoted Price in a job.
                    <br />
                    <br />
                    Invoiced Amount revenue calculations will grant the ability
                    to add invoice history to a job. The revenue calculations
                    will be based off of the total invoiced amount.</b-tooltip
                  >
                </label>
                <div class="col-lg-10">
                  <b-form-group>
                    <b-form-radio
                      v-for="(type, typeIndex) in revenueTypes"
                      :key="typeIndex"
                      v-model="selectedRevenueType"
                      :name="type.label"
                      :value="type.value"
                    >
                      {{ type.label }}
                    </b-form-radio>
                  </b-form-group>
                </div>
              </div>

              <div class="form-group row mb-4">
                <label class="col-form-label col-lg-2"
                  >Job Budgets Enabled
                </label>
                <div class="col-lg-10">
                  <switches
                    v-model="controlsEnabled"
                    type-bold="false"
                    color="primary"
                    class="ml-1 mb-0 mt-2"
                  ></switches>
                </div>
              </div>

              <div class="form-group row mb-4">
                <label class="col-form-label col-lg-2" id="lbl_addJob_enableFutureTimes">Allow Adding Future Times to This Job?
                  <i class="bx bxs-help-circle"></i>
                  <b-tooltip target="lbl_addJob_enableFutureTimes">
                    If disabled, this will not let you add times to the job where the date is set past the current date.
                  </b-tooltip>
                </label>
                <div class="col-lg-10">
                  <switches
                      v-model="futureTimesEnabled"
                      type-bold="false"
                      color="primary"
                      class="ml-1 mb-0 mt-2"
                    ></switches>
                </div>
              </div>

              <div v-if="controlsEnabled" class="form-group row mb-4">
                <div class="offset-lg-2 col-lg-7">
                  <job-budgets
                    :controls="controls"
                    :procurementControls="procurementControls"
                    :categoryBudgetsEnabled="false"
                    @jobBudgetUpdated="jobBudgetUpdated"
                  ></job-budgets>
                </div>
              </div>
            </form>
            <div class="row justify-content-end">
              <div class="col-lg-10">
                <button
                  type="submit"
                  class="btn btn-primary"
                  v-on:click="createJob"
                >
                  Create Project
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- end row -->
    <b-modal
      id="modal_addCustomer"
      title="Add Customer"
      title-class="font-18"
      ok-title="Add Customer"
      @ok="validateAddCustomer"
    >
      <div class="mb-4">
        <div class="form-group">
          <label>Customer Name</label>
          <b-form-input
            placeholder="Name..."
            v-model="addCustomer_name"
            :class="{ 'is-invalid': addCustomer_errors.customerError }"
          ></b-form-input>
          <div v-if="addCustomer_errors.customerError" class="invalid-feedback">
            <span>{{ addCustomer_errors.customerMessage }}</span>
          </div>
        </div>
      </div>
    </b-modal>
  </Layout>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import JobBudgets from '../components/jobs/JobBudgets.vue';
import Layout from '../../../layouts/main';
import PageHeader from '@/components/page-header';
import appConfig from '@/app.config';
import Swal from 'sweetalert2';
import Switches from 'vue-switches';

//import firebase from 'firebase';
import { firestore } from 'firebase';
import {
  getCustomers,
  addCustomer,
  getEmployees,
} from '../helper/dbHelpers.js';

/**
 * Projects-create component
 */
export default {
  page: {
    title: 'Create New Project',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    DatePicker,
    Layout,
    PageHeader,
    Switches,
    JobBudgets,
  },
  data() {
    return {
      title: 'Create New',
      items: [
        {
          text: 'home',
          href: '/',
        },
        {
          text: 'create job',
          href: '/production/jobs/createJob',
          active: true,
        },
      ],
      dropzoneOptions: {
        url: 'https://httpbin.org/post',
        thumbnailWidth: 150,
        maxFilesize: 0.5,
        headers: { 'My-Awesome-Header': 'header value' },
      },
      fields: [{ key: 'Customer', sortable: true }],
      date: null,
      customers: [],
      employees: [],
      projectCustomer: null,
      jobDescription: null,
      jobNumber: null,

      addJob_errors: {
        jobNumError: null,
        jobNumMessage: null,
        descError: null,
        descMessage: null,
        customerError: null,
        customerMessage: null,
        dateError: null,
        dateMessage: null,
      },
      addCustomer_name: null,
      addCustomer_errors: {
        customerError: false,
        customerMessage: null,
      },
      controlsEnabled: false,
      jobBudget: null,
      supervisor: null,
      controls: {
        labour: {
          active: false,
          type: 'Dollar Amount',
          unitOfMeasure: 'Billable Rate',
          value: null,
          name: 'Labour',
          types: ['Dollar Amount'],
          unitOfMeasures: ['Billable Rate', 'Payroll Rate'],
        },
        products: {
          active: false,
          type: 'Dollar Amount',
          unitOfMeasure: 'Billable Rate',
          value: null,
          name: 'Products',
          types: ['Dollar Amount'],
          unitOfMeasures: ['Billable Rate'],
        },
        procurements: {
          active: false,
          type: 'Dollar Amount',
          unitOfMeasure: 'Marked-up Amount',
          value: null,
          name: 'Procurements',
          types: ['Dollar Amount'],
          unitOfMeasures: ['Marked-up Amount', 'Received Amount'],
        },
      },
      procurementControls: {
        thirdParty: {
          active: false,
          type: 'Dollar Amount',
          unitOfMeasure: 'Marked-up Amount',
          value: null,
          name: '3rd Party',
          types: ['Dollar Amount'],
          unitOfMeasures: ['Marked-up Amount', 'Received Amount'],
        },
        consumables: {
          active: false,
          type: 'Dollar Amount',
          unitOfMeasure: 'Marked-up Amount',
          value: null,
          name: 'Consumables',
          types: ['Dollar Amount'],
          unitOfMeasures: ['Marked-up Amount', 'Received Amount'],
        },
        materials: {
          active: false,
          type: 'Dollar Amount',
          unitOfMeasure: 'Marked-up Amount',
          value: null,
          name: 'Materials',
          types: ['Dollar Amount'],
          unitOfMeasures: ['Marked-up Amount', 'Received Amount'],
        },
        other: {
          active: false,
          type: 'Dollar Amount',
          unitOfMeasure: 'Marked-up Amount',
          value: null,
          name: 'Other',
          types: ['Dollar Amount'],
          unitOfMeasures: ['Marked-up Amount', 'Received Amount'],
        },
        rentals: {
          active: false,
          type: 'Dollar Amount',
          unitOfMeasure: 'Marked-up Amount',
          value: null,
          name: 'Rentals',
          types: ['Dollar Amount'],
          unitOfMeasures: ['Marked-up Amount', 'Received Amount'],
        },
      },
      jobQuote: null,
      jobType: null,
      max: 1000000000,
      jobTypeOptions: ['Quoted (Fixed Rate)', 'Time x Material'],
      revenueTypes: [
        { label: 'Default', value: 'default' },
        { label: 'Invoiced Amount', value: 'invoices' },
      ],
      selectedRevenueType: 'default',
      jobDepartment: null,
      futureTimesEnabled: false
    };
  },
  methods: {
    createJob: async function () {
      let flag = true;
      for (const control in this.controls) {
        let data = this.controls[control];
        if (data.value !== null) {
          if (this.validateEntry(data.value) === false) {
            if (data.value !== '') {
              flag = false;
            }
          }
        }
        if (data.active) {
          if (data.value === null || data.value.trim() === '') {
            data.active = false;
            data.value = null;
          }
        }
        //remove the types fields so they aren't added to the db.
        delete this.controls[control].types;
        delete this.controls[control].unitOfMeasures;
      }

      //loop the procurement controls and format them for the database.
      for (const control in this.procurementControls) {
        let data = this.procurementControls[control];
        if (data.value !== null) {
          if (this.validateEntry(data.value) === false) {
            if (data.value !== '') {
              flag = false;
            }
          }
        }
        if (data.active) {
          if (data.value === null || data.value.trim() === '') {
            data.active = false;
            data.value = null;
          }
        }
        //remove the types fields so they aren't added to the db.
        delete this.procurementControls[control].types;
        delete this.procurementControls[control].unitOfMeasures;
      }
      let jobValid = await this.validateCreateJob();
      if (jobValid && flag) {
        firestore()
          .collection('jobs')
          .add({
            jobNum: Number(this.jobNumber),
            description: this.jobDescription,
            customerDisplayName: this.projectCustomer,
            customer: this.projectCustomer.toLowerCase(),
            date: this.date,
            org: sessionStorage.getItem('org'),
            controls: this.controls,
            supervisor: this.supervisor,
            procurementControls: this.procurementControls,
            jobBudget: this.jobBudget,
            jobType: this.jobType,
            jobRevenueCalculations: this.selectedRevenueType,
            completed: false,
            jobQuote:
              this.jobType === 'Quoted (Fixed Rate)'
                ? this.jobQuote
                  ? this.jobQuote
                  : '0'
                : null,
            jobDepartment: this.jobDepartment === '' ? null : this.jobDepartment,
            futureTimesEnabled: this.futureTimesEnabled
          })
          .then(() => {
            this.$router.push({
              path: `/production/jobs/jobDetails/${this.jobNumber}`,
            });
          })
          .catch((error) => {
            console.log(`Error creating the job. ${error}`);
            Swal.fire(
              'Error!',
              'An error was encountered while creating the job.',
              'error'
            );
          });
      }
    },
    validateCreateJob: async function () {
      let flag = true;
      //jobNum not empty
      if (this.jobNumber === null || this.jobNumber.toString().trim() === '') {
        this.addJob_errors.jobNumError = true;
        this.addJob_errors.jobNumMessage = 'Job Number cannot be empty.';
        flag = false;
      }
      //jobNum a number
      else if (isNaN(this.jobNumber)) {
        this.addJob_errors.jobNumError = true;
        this.addJob_errors.jobNumMessage = 'Job Number must be a number.';
        flag = false;
      } else {
        //jobNum not already used
        let jobNumberValid = await firestore()
          .collection('jobs')
          .where('org', '==', sessionStorage.getItem('org'))
          .where('jobNum', '==', Number(this.jobNumber))
          .get();
        if (!jobNumberValid.empty) {
          this.addJob_errors.jobNumError = true;
          this.addJob_errors.jobNumMessage =
            'Job Number has already been used.';
          flag = false;
        } else {
          this.addJob_errors.jobNumError = false;
          this.addJob_errors.jobNumMessage = null;
        }
      }
      //desc not empty
      if (this.jobDescription == null || this.jobDescription.trim() == '') {
        this.addJob_errors.descError = true;
        this.addJob_errors.descMessage = 'Job Description cannot be empty.';
        flag = false;
      } else {
        this.addJob_errors.descError = false;
        this.addJob_errors.descMessage = null;
      }
      //customer not empty
      if (this.projectCustomer == null) {
        this.addJob_errors.customerError = true;
        this.addJob_errors.customerMessage = 'Customer cannot be empty.';
        flag = false;
      } else {
        this.addJob_errors.customerError = false;
        this.addJob_errors.customerMessage = null;
      }
      //date not empty
      if (this.date == null || this.date == '') {
        this.addJob_errors.dateError = true;
        this.addJob_errors.dateMessage = 'Date cannot be empty.';
        flag = false;
      } else {
        this.addJob_errors.dateError = false;
        this.addJob_errors.dateMessage = null;
      }
      return flag;
    },
    writeCustomer: async function (valid) {
      if (valid) {
        addCustomer(this.addCustomer_name)
          .then(() => {
            //add the user to the drop down.
            Swal.fire('Success!', 'Customer added successfully.', 'success');
            this.customers.push(this.addCustomer_name);
          })
          .catch(() => {
            Swal.fire(
              'Error!',
              'An error was encounted when creating the customer.',
              'error'
            );
          });
      }
    },
    validateAddCustomer: async function (bvModalEvent) {
      let flag = true;
      bvModalEvent.preventDefault();
      if (
        this.addCustomer_name === null ||
        this.addCustomer_name.trim() === ''
      ) {
        this.addCustomer_errors.customerError = true;
        this.addCustomer_errors.customerMessage = 'Value cannot be empty.';
        flag = false;
      } else {
        // Check to see if customer name has been used for this org already
        let userInputLowercase = this.addCustomer_name.toLowerCase();
        const customerNameExists = this.customers.find(
          (customer) => customer.toLowerCase() === userInputLowercase
        );
        if (customerNameExists) {
          this.addCustomer_errors.customerError = true;
          this.addCustomer_errors.customerMessage = 'Customer already exists.';
          flag = false;
        }
      }
      if (flag) {
        this.addCustomer_errors.customerError = false;
        this.addCustomer_errors.customerMessage = null;
        await this.writeCustomer(flag);
        this.projectCustomer = this.addCustomer_name;
        // manually hide modal
        this.$bvModal.hide('modal_addCustomer');
      }
    },
    validateEntry(entry) {
      //check that value inputted is a number
      if (!isNaN(Number(entry)) && entry.trim() !== '') {
        return true;
      } else {
        return false;
      }
    },
    jobBudgetUpdated(value) {
      this.jobBudget = value;
    },
    getEmployeesFunction: async function () {
      await getEmployees()
        .then((querySnapshot) => {
          querySnapshot.forEach((doc) => {
            this.employees.push({
              name: doc.data().displayName,
              id: doc.id,
            });
          });
        })
        .catch(() => {
          console.log(`Error getting employees`);
        });
    },
    async getNextJobNumber() {
      let lastJob = await firestore()
        .collection('jobs')
        .where('org', '==', sessionStorage.getItem('org'))
        .orderBy('jobNum', 'desc')
        .limit(1)
        .get();

      lastJob.forEach((doc) => {
        this.jobNumber = doc.data().jobNum + 1;
      });
    },
  },
  mounted: function () {
    getCustomers()
      .then((querySnapshot) => {
        querySnapshot.forEach((doc) => {
          this.customers.push(doc.data().displayName);
        });
      })
      .catch(() => {
        console.log(`Error getting customers`);
      });
    this.getEmployeesFunction();
    this.getNextJobNumber();
  },
};
</script>

<style scoped>
.no-pointer {
  pointer-events: none;
}
.disabled-checkbox {
  cursor: not-allowed;
  opacity: 0.2;
  background-color: black !important;
}
.disabled-textbox {
  background: #e9e7e7;
}
</style>
